import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Dispatch, SetStateAction, useState } from 'react';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { useStore } from '../../state/zustand-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ModuleList } from '../../../shared/analytics/module-list';
import { handleErrors } from '../../../utils/errors';
import { UserKeys, bulkUpdateDriveMonitorStatus } from '../../../api/users-api';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import * as analytics from '../../../shared/analytics/analytics.json';
import { DriveSweepModel } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface EnableDriveSweepDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDriveSweeps: DriveSweepModel[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function EnableDriveSweepDialog({
  open,
  handleClose,
  selectedDriveSweeps,
  setSelectedRows,
  changeLogRoles,
}: EnableDriveSweepDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();

  const sweepIds = selectedDriveSweeps.map((sweep) => sweep.folderId);
  const moreThanOneRule = selectedDriveSweeps.length > 1;

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      bulkUpdateDriveMonitorStatus(sweepIds, true, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Drive Sweep${
          moreThanOneRule ? 's' : ''
        }  enabled successfully!`,
      });

      queryClient.setQueryData(
        [UserKeys.DriveSweepModels],
        (oldData: DriveSweepModel[]) => {
          if (oldData) {
            return oldData.map((sweep) => {
              if (sweepIds.includes(sweep.folderId)) {
                sweep.isActive = true;
              }
              return sweep;
            });
          }
        }
      );

      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: `There was an issue enabling the Drive Sweep${
          moreThanOneRule ? 's' : ''
        }. Please refresh and try again.`,
      });
    },
    onSettled: () => {
      createEvent(analytics.drive.drive_sweep.enable_drive_sweep, {
        module: ModuleList.modules.drive.name,
        section: ModuleList.modules.drive.sections.driveSweep,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DriveRoles.UpdateDriveSweepStatus.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Enable Drive Sweep${moreThanOneRule ? 's' : ''}`}
      dialogContent={
        <Box>
          <Typography>
            {moreThanOneRule ? (
              <>
                Do you wish to enable the selected{' '}
                <strong>{selectedDriveSweeps.length}</strong> sweeps?
              </>
            ) : (
              <>
                Do you wish to enable drive sweep{' '}
                <strong>{selectedDriveSweeps[0].name}</strong>?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: `Enable sweep${moreThanOneRule ? 's' : ''}`,
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
